import { useState, useEffect, useCallback, useMemo } from "react";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";

/**
 * Custom hook for unit management operations
 */
export const useUnits = () => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cache management
  const [cache, setCache] = useState({
    data: [],
    lastFetch: null,
    isStale: false,
  });

  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Cache validation
  const isCacheValid = useCallback(() => {
    if (!cache.lastFetch) return false;
    return Date.now() - cache.lastFetch < CACHE_DURATION && !cache.isStale;
  }, [cache.lastFetch, cache.isStale]);

  // Memoized parent unit lookup map for O(1) access
  const parentUnitMap = useMemo(() => {
    const map = new Map();
    units.forEach((unit) => {
      map.set(unit.unitId, unit.unitName);
    });
    return map;
  }, [units]);

  // Helper function to convert Firestore timestamps to readable dates
  const formatFirestoreDate = useCallback((timestamp) => {
    if (!timestamp) return "לא זמין";

    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString("he-IL");
    }

    if (timestamp instanceof Date) {
      return timestamp.toLocaleString("he-IL");
    }

    try {
      return new Date(timestamp).toLocaleString("he-IL");
    } catch (error) {
      return "לא זמין";
    }
  }, []);

  // Real-time units listener with caching
  useEffect(() => {
    // Check cache first
    if (isCacheValid() && cache.data.length > 0) {
      setUnits(cache.data);
      setLoading(false);
      setError(null);
      return;
    }

    const unitsRef = collection(db, "units");
    const q = query(unitsRef, orderBy("createdAt", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const unitsData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            unitId: data.unitId,
            unitName: data.unitName,
            parentUnit: data.parentUnit,
            level: data.level,
            status: data.status,
            createdAt: formatFirestoreDate(data.createdAt),
            updatedAt: formatFirestoreDate(data.updatedAt),
          };
        });

        // Update cache
        setCache({
          data: unitsData,
          lastFetch: Date.now(),
          isStale: false,
        });

        setUnits(unitsData);
        setLoading(false);
        setError(null);
      },
      (error) => {
        console.error("Error fetching units:", error);
        setError("שגיאה בטעינת היחידות");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [formatFirestoreDate, isCacheValid, cache.data, cache.lastFetch]);

  // Add unit
  const addUnit = async (unitData) => {
    try {
      // Generate unique unit ID
      const unitId = `UNIT_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;

      const newUnitData = {
        ...unitData,
        unitId,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const unitRef = doc(db, "units", unitId);
      await setDoc(unitRef, newUnitData);

      // Invalidate cache
      setCache((prev) => ({ ...prev, isStale: true }));

      return { success: true };
    } catch (error) {
      console.error("Error adding unit:", error);
      return { success: false, error: "שגיאה בהוספת יחידה" };
    }
  };

  // Update unit
  const updateUnit = async (unitId, updateData) => {
    try {
      const unitRef = doc(db, "units", unitId);
      const finalUpdateData = {
        ...updateData,
        updatedAt: new Date(),
      };

      await updateDoc(unitRef, finalUpdateData);

      // Invalidate cache
      setCache((prev) => ({ ...prev, isStale: true }));

      return { success: true };
    } catch (error) {
      console.error("Error updating unit:", error);
      return { success: false, error: "שגיאה בעדכון יחידה" };
    }
  };

  // Delete unit
  const deleteUnit = async (unitId) => {
    try {
      await deleteDoc(doc(db, "units", unitId));

      // Invalidate cache
      setCache((prev) => ({ ...prev, isStale: true }));

      return { success: true };
    } catch (error) {
      console.error("Error deleting unit:", error);
      return { success: false, error: "שגיאה במחיקת יחידה" };
    }
  };

  // Get parent unit name by ID - optimized with O(1) lookup
  const getParentUnitName = useCallback(
    (parentUnitId) => {
      if (!parentUnitId) return "-";
      return parentUnitMap.get(parentUnitId) || "לא נמצא";
    },
    [parentUnitMap]
  );

  return {
    units,
    loading,
    error,
    addUnit,
    updateUnit,
    deleteUnit,
    getParentUnitName,
  };
};
